"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.buildEntityScriptHelpers = buildEntityScriptHelpers;
const computeEntityListRows_1 = require("./computeEntityListRows");
const characterMap_1 = require("../engine/characterMap");
/**
 * Read-only + mutation helpers for entity scripts. Mutations go through {@link EntitiesServiceInterface}.
 */
function buildEntityScriptHelpers(entitiesService, variableStore, player, gameSession, adventureManager) {
    const getAdventureEntityTypes = () => {
        if (!adventureManager)
            return [];
        const adv = adventureManager.getAdventure(gameSession.adventureId);
        return adv?.entityTypes ?? [];
    };
    const resolveVarType = (uuid, name, value) => {
        const existing = entitiesService?.getEntityVariable(uuid, name);
        if (existing?.type === 'character_map') {
            throw new Error(`Variable '${name}' is a character_map. Use setCharacterMapValue to set this character's slot, or resetCharacterMap to wipe the map.`);
        }
        if (existing?.type === 'number' || existing?.type === 'boolean' || existing?.type === 'string') {
            return existing.type;
        }
        const types = getAdventureEntityTypes();
        const inst = entitiesService?.getEntity(uuid);
        const decl = types.find(t => t.id === inst?.typeId)?.variables.find(v => v.name === name);
        if (decl?.type === 'character_map') {
            throw new Error(`Variable '${name}' is a character_map. Use setCharacterMapValue to set this character's slot, or resetCharacterMap to wipe the map.`);
        }
        if (decl?.type === 'number' || decl?.type === 'boolean' || decl?.type === 'string') {
            return decl.type;
        }
        if (typeof value === 'number')
            return 'number';
        if (typeof value === 'boolean')
            return 'boolean';
        return 'string';
    };
    return {
        currentEntity() {
            if (!entitiesService)
                return '';
            return entitiesService.getCurrentEntityId(player.id) ?? '';
        },
        entityExists(uuid) {
            if (!entitiesService)
                return false;
            return !!entitiesService.getEntity(String(uuid ?? '').trim());
        },
        getEntityType(uuid) {
            if (!entitiesService)
                return '';
            const id = String(uuid ?? entitiesService.getCurrentEntityId(player.id) ?? '').trim();
            return entitiesService.getEntity(id)?.typeId ?? '';
        },
        getEntityParent(uuid) {
            if (!entitiesService)
                return '';
            const id = String(uuid ?? entitiesService.getCurrentEntityId(player.id) ?? '').trim();
            return entitiesService.getEntity(id)?.parentEntityId ?? '';
        },
        getEntityDisplayName(uuid) {
            if (!entitiesService)
                return '';
            const id = String(uuid ?? entitiesService.getCurrentEntityId(player.id) ?? '').trim();
            if (!id)
                return '';
            const typeId = entitiesService.getEntity(id)?.typeId;
            const typeName = getAdventureEntityTypes().find(t => t.id === typeId)?.displayName;
            return entitiesService.getDisplayName(id, typeName);
        },
        createEntity(typeId, parentUuid) {
            if (!entitiesService) {
                throw new Error('createEntity requires an EntitiesService on the game engine (session entities not configured).');
            }
            return entitiesService.createEntity({
                typeId: String(typeId ?? '').trim(),
                parentEntityId: parentUuid !== undefined && parentUuid !== null ? String(parentUuid).trim() : undefined,
                playerId: player.id
            });
        },
        destroyEntity(uuid) {
            if (!entitiesService) {
                throw new Error('destroyEntity requires an EntitiesService on the game engine (session entities not configured).');
            }
            const id = String(uuid ?? '').trim();
            if (!id) {
                throw new Error('destroyEntity failed: uuid is required (pass an id or currentEntity()).');
            }
            return entitiesService.destroyEntity(id);
        },
        setCurrentEntity(uuid) {
            if (!entitiesService) {
                throw new Error('setCurrentEntity requires an EntitiesService on the game engine (session entities not configured).');
            }
            const id = String(uuid ?? '').trim();
            if (!id) {
                throw new Error('setCurrentEntity failed: uuid is required.');
            }
            const ok = entitiesService.setCurrentEntity(player.id, id);
            if (!ok) {
                throw new Error(`setCurrentEntity failed: entity '${id}' does not exist in this session.`);
            }
            return ok;
        },
        clearCurrentEntity() {
            if (!entitiesService)
                return;
            entitiesService.clearCurrentEntity(player.id);
        },
        getEntityVariable(name, uuid) {
            if (!entitiesService)
                return undefined;
            const id = String(uuid ?? entitiesService.getCurrentEntityId(player.id) ?? '').trim();
            const variable = entitiesService.getEntityVariable(id, String(name ?? '').trim());
            if (!variable) {
                return undefined;
            }
            return (0, characterMap_1.unwrapEntityVariableValue)(variable, player.characterName);
        },
        getEntityCharacterMap(name, uuid) {
            if (!entitiesService)
                return {};
            const explicitUuid = uuid !== undefined && uuid !== null ? String(uuid).trim() : '';
            const id = explicitUuid || String(entitiesService.getCurrentEntityId(player.id) ?? '').trim();
            const varName = String(name ?? '').trim();
            if (!id || !varName || !entitiesService.getEntity(id)) {
                return {};
            }
            try {
                return entitiesService.getEntityCharacterMap(id, varName);
            }
            catch {
                return {};
            }
        },
        setEntityVariable(name, value, uuid) {
            if (!entitiesService) {
                throw new Error('setEntityVariable requires an EntitiesService on the game engine (session entities not configured).');
            }
            const explicitUuid = uuid !== undefined && uuid !== null ? String(uuid).trim() : '';
            const id = explicitUuid || String(entitiesService.getCurrentEntityId(player.id) ?? '').trim();
            const varName = String(name ?? '').trim();
            if (!varName) {
                throw new Error('setEntityVariable failed: variable name is required.');
            }
            if (!id) {
                throw new Error('setEntityVariable failed: no current entity. Call setCurrentEntity(uuid) first, or pass a uuid argument.');
            }
            if (!entitiesService.getEntity(id)) {
                throw new Error(`setEntityVariable failed: entity '${id}' does not exist (it may have been destroyed).`);
            }
            const type = resolveVarType(id, varName, value);
            const ok = entitiesService.setEntityVariable(id, varName, value, type);
            if (!ok) {
                throw new Error(`setEntityVariable failed for entity '${id}' variable '${varName}'.`);
            }
            // Keep VariableStore in sync when current entity matches (optional cache)
            if (variableStore && id && entitiesService.getCurrentEntityId(player.id) === id) {
                // Entity vars resolve via entitiesService in getVariable when wired; no-op here for Phase 1 purity.
            }
        },
        /**
         * Pure helper for tests / authors: compute list rows for an Entity-List-like filter.
         */
        computeEntityListForScript(typesCsv, parentUuid) {
            if (!entitiesService)
                return [];
            const types = (0, computeEntityListRows_1.parseEntityListTypesAttr)(typesCsv);
            let parentEntityId;
            if (parentUuid === null) {
                parentEntityId = null;
            }
            else if (parentUuid === undefined) {
                parentEntityId = null; // default top-level for script helper
            }
            else if (String(parentUuid).trim() === 'current') {
                parentEntityId = entitiesService.getCurrentEntityId(player.id) ?? '';
            }
            else {
                parentEntityId = String(parentUuid).trim();
            }
            return (0, computeEntityListRows_1.computeEntityListRows)(entitiesService.listEntities(), getAdventureEntityTypes(), entitiesService, { types, parentEntityId, sort: 'name' });
        }
    };
}
//# sourceMappingURL=entityScriptHelpers.js.map